//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import {
  Star,
  StarFilled,
  Settings,
  ChevronDown,
  ChevronUp,
} from '@carbon/icons-react';
import Plan from '../../icons/PlanLoopCard.svg';
import Test from '../../icons/TestLoopCard.svg';
import VS from '../../icons/VelocityLoopCard.svg';
import Deploy from '../../icons/DeployLoopCard.svg';
import SC from '../../icons/ControlLoopCard.svg';
import AI from '../../icons/AIChatLogo.svg';
import Build from '../../icons/BuildLoopCard.svg';
import { useTranslation } from 'react-i18next';
import LoopDataService from '../../utils/LoopDataService';
import EditModal from './Edit/EditModal';
import AIModule, { ChatMessage } from './AI/AIModule';
import UpgradeLoopModal from './UpgradeLoopModal';
import { useConfig } from '../../Config';
import { useNavigate } from 'react-router-dom';
import { useLoopContext } from '../../LoopContext';
import { useActiveLoopContext } from '../../ActiveLoopContext';
import { useUserAuth } from '../../UserAuth';
import type { LoopLinks } from '../Sidebar/SidebarTypes';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { setLoopLinksResponse } from './response';
import { Skeleton } from '../ui/skeleton';
import { useDashboardIframePolling } from './useDashboardIframePolling';
import { useDashboardRerenderInterval } from './useDashboardRerenderInterval';
import { sanitizeIframeSrc } from '../../utils/securityUtils';
import CustomSnackbar, {
  useSnackbar,
} from '../../components/common/CustomSnackbar';
import { isSafeRedirectUrl } from '../../utils/validateUrlUtils';
import { cn } from '../../lib/utils';
import type {
  UpgradeTaskSummary,
  UpgradePreviewTask,
  UpgradeResultPayload,
} from './upgradeTypes';

interface CardProps {
  id: string;
  name: string;
  description?: string;
  isAdmin?: boolean;
  isFavorite?: boolean;
  onToggleFavorite: (id: string) => void;
  isDisabled?: boolean;
  toggleDisableLoop: (id: string) => void;
  loadingDisableIds?: string[];
  refreshLoops: () => void;
  teamspaceId: string;
  associatedUsers: string[];
  // When true, show an upgrade indicator in the card UI.
  upgradable?: boolean;
  sharedChatHistories: { [loopId: string]: ChatMessage[] };
  setSharedChatHistories: React.Dispatch<
    React.SetStateAction<{ [loopId: string]: ChatMessage[] }>
  >;
  viewDetailedDashboard: (data: any, loopName?: string) => void;
  detailedDashboardUrl: (data: any) => void;
  incompelete?: boolean;
  failed?: boolean;
  isTeamspaceAdmin?: boolean;
  isAnyAIModuleVisible?: boolean;
  onAIModuleVisibilityChange?: (visible: boolean) => void;
}

interface IconButtonProps {
  icon: string;
  label: string;
  showSeparator?: boolean;
  marginLeft?: string;
}

const IconButton: React.FC<IconButtonProps> = ({
  icon,
  label,
  showSeparator = true,
  marginLeft = '0px',
}) => (
  <div
    id="id-loop-card-icon-button-wrapper"
    data-testid="loop-card-icon-button-wrapper"
    className="flex items-center gap-2"
    style={{ marginLeft }}
  >
    <img src={icon} alt={label} className="size-4" />
    <button
      className="border-none bg-transparent p-0 text-sm text-gray-700 dark:text-gray-300"
      onClick={() => {}}
    >
      {label}
    </button>
    {showSeparator && (
      <span
        id="id-loop-card-icon-button-separator"
        data-testid="loop-card-icon-button-separator"
        className="text-gray-400 dark:text-gray-600"
      >
        |
      </span>
    )}
  </div>
);

const toTaskDisplayName = (value: string | undefined): string => {
  if (!value) {
    return 'Upgrade Task';
  }
  return value
    .toLowerCase()
    .split('_')
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
};

const normalizeUpgradeTasks = (response: any): UpgradeTaskSummary[] => {
  // Spring HATEOAS wraps lists under _embedded with a key derived from the
  // entity class name. Try all known variants before falling back.
  const embedded = response?._embedded;
  const rawTasks = Array.isArray(response)
    ? response
    : embedded?.loopTaskEntityList ??
      embedded?.loopTaskEntities ??
      embedded?.tasks ??
      response?.content ??
      [];

  if (!Array.isArray(rawTasks)) {
    return [];
  }

  return rawTasks.map((rawTask: any, index: number) => {
    const task = rawTask?.content ?? rawTask ?? {};
    const taskType = task?.type ?? rawTask?.type;
    const executionResult = task?.executionResult ?? rawTask?.executionResult;
    const fallbackName =
      typeof executionResult === 'string' && executionResult
        ? executionResult
        : undefined;

    return {
      id: String(
        task?.id ?? task?.taskId ?? rawTask?.id ?? rawTask?.taskId ?? index,
      ),
      name: fallbackName || toTaskDisplayName(taskType),
      status: String(task?.status ?? rawTask?.status ?? 'UNKNOWN'),
      loopVersionAtExecution:
        task?.loopVersionAtExecution ??
        rawTask?.loopVersionAtExecution ??
        task?.targetVersion ??
        rawTask?.targetVersion,
    };
  });
};

const normalizeUpgradePreviewTasks = (response: any): UpgradePreviewTask[] => {
  const rawTasks = Array.isArray(response)
    ? response
    : response?._embedded?.tasks || response?.content || [];

  if (!Array.isArray(rawTasks)) {
    return [];
  }

  return rawTasks.map((rawTask: any, index: number) => {
    const item = rawTask?.content ?? rawTask ?? {};
    const taskType = item?.type ?? rawTask?.type;
    const fallbackName =
      typeof taskType === 'string' && taskType
        ? toTaskDisplayName(taskType)
        : 'Upgrade Task';

    return {
      id: String(item?.latestTaskId ?? item?.type ?? rawTask?.id ?? index),
      name: String(item?.displayName ?? rawTask?.displayName ?? fallbackName),
      action: String(item?.action ?? rawTask?.action ?? 'EXECUTE'),
      latestStatus: String(
        item?.latestStatus ?? rawTask?.latestStatus ?? 'NOT_STARTED',
      ),
      reason: String(item?.reason ?? rawTask?.reason ?? ''),
      targetVersion: item?.targetVersion ?? rawTask?.targetVersion,
    };
  });
};

/**
 * Card component represents a loop card with various actions and details.
 *
 * @param {CardProps} props - The properties for the Card component.
 */

const Card: React.FC<CardProps> = ({
  id,
  name,
  description,
  isAdmin,
  isFavorite,
  onToggleFavorite,
  isDisabled,
  toggleDisableLoop,
  loadingDisableIds,
  refreshLoops,
  teamspaceId,
  associatedUsers,
  upgradable,
  sharedChatHistories,
  setSharedChatHistories,
  viewDetailedDashboard,
  detailedDashboardUrl,
  incompelete,
  failed,
  isTeamspaceAdmin,
  isAnyAIModuleVisible,
  onAIModuleVisibilityChange,
}) => {
  const { t } = useTranslation();
  const { services } = useConfig();
  const { baseName } = useConfig();

  const navigate = useNavigate();
  const { setRetryLoop, setSelectedLoop, setLoopLinksResponseInContext } =
    useLoopContext();
  const { setLoopContext } = useActiveLoopContext();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [isEditModalVisible, setEditModalVisible] = useState(false);
  const [isAIModuleVisible, setAIModuleVisible] = useState(false);
  const { user, licenses, isAuthenticated } = useUserAuth();

  const loopDataService = new LoopDataService(
    () => {
      return user;
    },
    () => {
      return isAuthenticated;
    },
  );
  const showAI = services?.analyticsai?.show;
  const showDisable = services?.disable?.show;
  const isDev = process.env.NODE_ENV === 'development';
  const effectiveIsTeamspaceAdmin = isDev ? true : isTeamspaceAdmin;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [content, setContent] = useState<LoopLinks | null>(null);
  const [overviewDashboard, setoverviewDashboard] = useState('');
  const [detailedDashboard, setdetailedDashboard] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [appDropdownOpen, setAppDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const appDropdownRef = useRef<HTMLDivElement>(null);
  const { teamspaceName } = useTeamspaceContext();

  const [dashboardLoaded, setDashboardLoaded] = useState(false);

  // Local upgrade flag and in-flight state so we can hide/disable
  // the upgrade action immediately after success and prevent
  // double-clicks while the API call is in progress.
  const [localUpgradable, setLocalUpgradable] = useState<boolean>(!!upgradable);
  const [upgradeInProgress, setUpgradeInProgress] = useState(false);
  const [isUpgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeTasks, setUpgradeTasks] = useState<UpgradeTaskSummary[]>([]);
  const [upgradePreviewTasks, setUpgradePreviewTasks] = useState<
    UpgradePreviewTask[]
  >([]);
  const [upgradePreviewLoading, setUpgradePreviewLoading] = useState(false);
  const [upgradePreviewError, setUpgradePreviewError] = useState('');
  const [selectedAppLabel, setSelectedAppLabel] = useState<string | null>(null);

  useEffect(() => {
    setLocalUpgradable(!!upgradable);
  }, [upgradable]);

  const loopLinks = async (teamspaceId: string, id: any) => {
    try {
      const response = await loopDataService.loopLink(teamspaceId, id);
      setContent(response);
      setLoopLinksResponse(
        response as any,
        setLoopLinksResponseInContext,
        name,
        teamspaceName,
      );
    } catch (error) {
      console.error('Error fetching loop links:', error);
    }
  };

  const handleAppDropdownToggle = () => {
    setAppDropdownOpen((prev) => {
      const willOpen = !prev;
      if (willOpen) {
        loopLinks(teamspaceId, id);
      }
      return willOpen;
    });
  };

  const handleAppOptionClick = async (label: string) => {
    // Map translation label to config key
    const labelToConfigKey: Record<string, string> = {
      [t('Plan')]: 'planLoop',
      [t('Test')]: 'testLoop',
      [t('valueStream')]: 'measureLoop',
      [t('Release')]: 'releaseLoop',
      [t('Deploy')]: 'deployLoop',
      [t('Control')]: 'controlLoop',
      [t('build')]: 'buildLoop',
    };
    const configKey = labelToConfigKey[label] || label;

    // Use the fetched content for loop links
    const loopLinkMap: Record<string, string | undefined> = {
      planLoop: content?.planLoop,
      testLoop: content?.testLoop,
      measureLoop: content?.measureLoop,
      releaseLoop: content?.releaseLoop,
      deployLoop: content?.deployLoop,
      controlLoop: content?.controlLoop,
      buildLoop: content?.buildLoop,
    };

    const validUrl = loopLinkMap[configKey];
    if (!validUrl) {
      setAppDropdownOpen(false);
      return;
    }
    setSelectedAppLabel(label);

    const internalRouteMap: Record<string, string> = {
      planLoop: '/link/plan',
      testLoop: '/link/test',
      measureLoop: '/link/measure',
      releaseLoop: '/link/release',
      deployLoop: '/link/deploy',
      controlLoop: '/link/control',
      buildLoop: '/link/build',
    };

    const internalPath = internalRouteMap[configKey];
    if (internalPath && validUrl) {
      // Extract just the path part from the validUrl (removing origin)
      const urlObj = new URL(validUrl, window.location.origin);
      const pathWithHash = urlObj.pathname + urlObj.hash;
      const embedUrl = `${window.location.origin}${baseName}${internalPath}?path=${encodeURIComponent(pathWithHash)}`;
      if (isSafeRedirectUrl(embedUrl)) {
        // Break taint chain: reconstruct validated URL via URL constructor (CWE-79)
        const validatedUrl = new URL(embedUrl, window.location.origin);
        if (validatedUrl.origin !== window.location.origin) return;
        window.open(
          `${validatedUrl.origin}${validatedUrl.pathname}${validatedUrl.search}${validatedUrl.hash}`,
          '_blank',
          'noopener,noreferrer',
        );
      } else {
        console.error(
          'Blocked unsafe link: URL failed isSafeRedirectUrl validation',
        );
      }
    }

    setAppDropdownOpen(false);
  };

  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  const showSnackbarForEditModal = (message: string, type?: string) => {
    showSnackbar(message, type as 'success' | 'error' | 'info' | 'warning');
  };

  const pendingUpgradeToastRef = useRef<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  } | null>(null);

  const handleUpgradeAnimationComplete = useCallback(() => {
    const pending = pendingUpgradeToastRef.current;
    if (pending) {
      pendingUpgradeToastRef.current = null;
      showSnackbarForEditModal(pending.message, pending.type);
    }
  }, []);

  const handleUpgradeClick = useCallback(async () => {
    if (!localUpgradable || !isAdmin || upgradeInProgress) {
      return;
    }

    setUpgradePreviewTasks([]);
    setUpgradePreviewError('');
    setUpgradePreviewLoading(true);
    setUpgradeModalOpen(true);

    try {
      const previewResponse = await loopDataService.getUpgradeTaskPreview(id);
      setUpgradePreviewTasks(normalizeUpgradePreviewTasks(previewResponse));
    } catch {
      setUpgradePreviewError(
        'Could not load upgrade preview tasks. You can still continue with upgrade.',
      );
    } finally {
      setUpgradePreviewLoading(false);
    }
  }, [id, isAdmin, localUpgradable, upgradeInProgress, loopDataService]);

  const handleUpgradeFromModal =
    useCallback(async (): Promise<UpgradeResultPayload> => {
      if (!localUpgradable || upgradeInProgress) {
        return { success: false, tasks: [] };
      }

      try {
        setUpgradeInProgress(true);
        const response = await loopDataService.upgradeLoop(id);
        const tasks = normalizeUpgradeTasks(response?.data ?? response);
        setUpgradeTasks(tasks);

        // 207 means the backend ran all tasks but at least one failed;
        // each task in the list carries its own status.
        const httpStatus = response?.status ?? response?.statusCode;
        const hasTaskFailures =
          httpStatus === 207 ||
          tasks.some((t) => t.status?.toUpperCase() === 'FAILED');

        if (hasTaskFailures) {
          pendingUpgradeToastRef.current = {
            message: t('upgrade-loop-failed'),
            type: 'error',
          };
          return { success: false, tasks };
        }

        // 202 = upgrade in progress (tasks are REQUESTED/STARTED)
        if (httpStatus === 202) {
          setLocalUpgradable(true); // Keep upgradable flag - tasks still running
          pendingUpgradeToastRef.current = {
            message: t('upgrade-loop-in-progress'),
            type: 'info',
          };
          return { success: true, tasks }; // Return success so modal shows progress
        }

        // 200 = already upgraded, 201 = freshly upgraded — both are success.
        setLocalUpgradable(false);
        pendingUpgradeToastRef.current = {
          message: t('upgrade-loop-success'),
          type: 'success',
        };

        // Don't refresh loops here - wait until modal closes
        // This prevents the parent from re-rendering and resetting isUpgradeModalOpen
        return { success: true, tasks };
      } catch (error: any) {
        const status = error?.status ?? error?.statusCode;
        // Try to extract a task list from error response body (e.g. 4xx with body)
        const errorTasks = normalizeUpgradeTasks(
          error?.data ?? error?.response?.data ?? {},
        );
        if (errorTasks.length > 0) {
          setUpgradeTasks(errorTasks);
        }
        if (status === 409) {
          showSnackbarForEditModal(t('upgrade-loop-in-progress'), 'info');
        } else if (status === 500) {
          showSnackbarForEditModal(t('upgrade-loop-failed'), 'error');
        } else {
          showSnackbarForEditModal(t('upgrade-loop-error'), 'error');
        }
        return { success: false, tasks: errorTasks };
      } finally {
        setUpgradeInProgress(false);
      }
    }, [
      id,
      localUpgradable,
      upgradeInProgress,
      loopDataService,
      showSnackbarForEditModal,
      t,
    ]);

  const [isHovered, setIsHovered] = useState(false);
  const [viewDetail] = useState(true);

  const [height, setHeight] = useState(435);
  const isResizing = useRef(false);
  const startY = useRef(0);
  const heightRef = useRef(300);

  const toggleFavorite = useCallback(() => {
    onToggleFavorite(id);
  }, [id, onToggleFavorite]);

  const toggleDropdown = useCallback(() => {
    setDropdownVisible((prev) => !prev);
  }, []);

  /**
   * Handles clicks outside the dropdown to close it.
   *
   * @param {MouseEvent} event - The mouse event.
   */

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setDropdownVisible(false);
    }

    if (
      appDropdownRef.current &&
      !appDropdownRef.current.contains(event.target as Node)
    ) {
      setAppDropdownOpen(false);
    }
  }, []);

  /**
   * Disables the loop and refreshes the list of loops.
   */

  const handleDisableLoop = useCallback(async () => {
    try {
      setDropdownVisible(false);
      toggleDisableLoop(id);
    } catch (error) {
      console.error('Error disabling loop:', error);
    }
  }, [id, teamspaceId, toggleDisableLoop, refreshLoops, loopDataService]);

  /**
   * Opens the edit modal.
   */

  const handleEditClick = useCallback(() => {
    setEditModalVisible(true);
    setDropdownVisible(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setEditModalVisible(false);
  }, []);

  const handleAIClick = useCallback(() => {
    setAIModuleVisible(true);
    onAIModuleVisibilityChange?.(true);
  }, [onAIModuleVisibilityChange]);

  const handleCloseAIModule = useCallback(() => {
    setAIModuleVisible(false);
    onAIModuleVisibilityChange?.(false);
  }, [onAIModuleVisibilityChange]);

  useEffect(() => {}, [isAIModuleVisible]);

  const handleRetryLoop = () => {
    const incomplete = {
      id: id,
      name: name,
      mode: 'loop',
      teamSpaceName: teamspaceName,
      registrationFailed: true,
    };
    setRetryLoop(incomplete);
    navigate('/loop');
  };

  const handleRetryIncompleteLoop = () => {
    const isFullRetry = !failed;
    const incomplete = {
      id: id,
      name: name,
      mode: 'loop',
      teamSpaceName: teamspaceName,
      incomplete: true,
      fullRetry: isFullRetry,
    };
    setRetryLoop(incomplete);
    navigate('/loop');
  };

  const fetchDashboardUrls = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await loopDataService.getDashboardUrl(teamspaceId, id);
      setoverviewDashboard(response?.Overview);
      setdetailedDashboard(response?.Detailed);
    } catch (error) {
      setError('Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchDashboardUrls();
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    isResizing.current = true;
    startY.current = e.clientY;
    heightRef.current = height;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing.current || e.buttons !== 1) return;

    const deltaY = e.clientY - startY.current;
    const newHeight = Math.max(heightRef.current + deltaY, 350);

    setHeight(newHeight); // Schedule state update
  };

  const handleMouseUp = () => {
    isResizing.current = false;
    heightRef.current = height; // Ensure ref is in sync with the final state

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useDashboardIframePolling(
    iframeRef,
    overviewDashboard,
    setDashboardLoaded,
    dashboardLoaded,
  );

  useDashboardRerenderInterval(setDashboardLoaded);

  useEffect(() => {
    // This stops most interaction with the dashboards, but allows scrolling with the
    // mouse wheel and hovering over visualizations. It prevents the cursor from changing
    // to a pointer when hovering over elements so that there is less suggestion they should be
    // clicked
    const onInteraction = (event: Event) => {
      event.stopPropagation();
      event.preventDefault();
    };

    if (iframeRef.current) {
      const iframe = iframeRef.current;
      iframe.addEventListener('keydown', onInteraction, true);
      iframe.addEventListener('click', onInteraction, true);
      iframe.addEventListener('mousedown', onInteraction, true);
      iframe.addEventListener('mouseup', onInteraction, true);

      const onDocumentLoad = () => {
        const content = iframeRef.current?.contentDocument;
        if (content) {
          const style = content.createElement('style');
          style.id = 'no-cursor-change';
          style.textContent = `
          body, body * {
            cursor: default !important;
          }`;

          content.head.appendChild(style);

          content.addEventListener('keydown', onInteraction, true);
          content.addEventListener('click', onInteraction, true);
          content.addEventListener('mousedown', onInteraction, true);
          content.addEventListener('mouseup', onInteraction, true);
        }
      };
      iframe.addEventListener('load', onDocumentLoad, true);

      return () => {
        const iframe = iframeRef.current;
        if (iframe) {
          iframe.removeEventListener('load', onDocumentLoad, true);
          iframe.removeEventListener('keydown', onInteraction, true);
          iframe.removeEventListener('click', onInteraction, true);
          iframe.removeEventListener('mousedown', onInteraction, true);
          iframe.removeEventListener('mouseup', onInteraction, true);
          iframe.removeEventListener('wheel', onInteraction, true);

          const content = iframe.contentDocument;
          if (content) {
            content.removeEventListener('keydown', onInteraction, true);
            content.removeEventListener('click', onInteraction, true);
            content.removeEventListener('mousedown', onInteraction, true);
            content.removeEventListener('mouseup', onInteraction, true);
          }
        }
      };
    }
  }, [iframeRef]);

  const viewDashboard: React.MouseEventHandler<HTMLSpanElement> = (event) => {
    viewDetailedDashboard(viewDetail, name);
    detailedDashboardUrl(detailedDashboard);
  };

  if (failed) {
    return (
      <div
        id="id-loop-card-failed-container"
        data-testid="loop-card-failed-container"
        className="mb-6 overflow-visible rounded-lg border border-[#e4e4f0] bg-white p-4 dark:border-[#3f3f4a] dark:bg-carbon-darkPanel"
      >
        <div
          id="id-loop-card-failed-header"
          data-testid="loop-card-failed-header"
          className="flex items-center justify-between p-2"
        >
          <span
            id="id-loop-card-failed-name"
            data-testid="loop-card-failed-name"
            className="text-sm font-semibold text-[#4a4a56] dark:text-[#c8c8dd]"
          >
            {name}
          </span>
          <div
            id="id-loop-card-failed-actions"
            data-testid="loop-card-failed-actions"
            className="flex items-center"
          >
            <button
              id="id-retry-now-button"
              data-testid="id-retry-now-button"
              type="button"
              onClick={handleRetryLoop}
              className="btn-primary h-[30px] px-3 text-sm"
            >
              {t('retry-now')}
            </button>
          </div>
        </div>
      </div>
    );
  } else if (incompelete) {
    return (
      <div
        id="id-loop-card-incomplete-container"
        data-testid="loop-card-incomplete-container"
        className="mb-6 overflow-visible rounded-lg border border-[#e4e4f0] bg-white p-4 dark:border-[#3f3f4a] dark:bg-carbon-darkPanel"
      >
        <div
          id="id-loop-card-incomplete-header"
          data-testid="loop-card-incomplete-header"
          className="flex items-center justify-between p-2"
        >
          <span
            id="id-loop-card-incomplete-name"
            data-testid="loop-card-incomplete-name"
            className="text-sm font-semibold text-[#4a4a56] dark:text-[#c8c8dd]"
          >
            {name}
          </span>
          <div
            id="id-loop-card-incomplete-actions"
            data-testid="loop-card-incomplete-actions"
            className="flex items-center"
          >
            <button
              id="id-retry-incomplete-now-button"
              data-testid="id-retry-incomplete-now-button"
              type="button"
              onClick={handleRetryIncompleteLoop}
              className="btn-primary h-[30px] px-4 text-sm"
            >
              {t('resume')}
            </button>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div
        data-testid="loop-card-container"
        id={`loop-card-${id}`}
        className="mb-6 overflow-visible rounded-lg border border-[#e4e4f0] bg-white p-6 dark:border-[#3f3f4a] dark:bg-carbon-darkPanel"
      >
        <div
          data-testid="loop-card-header"
          id={`loop-header-${id}`}
          className="mb-6 flex items-center justify-between"
        >
          <div
            id="id-loop-card-header-left"
            data-testid="loop-card-header-left"
            className="flex items-center gap-2"
          >
            {!isDisabled && ( // Show favorite icon only if loop is not disabled
              <div
                id="id-loop-card-favorite-wrapper"
                data-testid="loop-card-favorite-wrapper"
                className="flex items-center gap-2"
              >
                {isFavorite ? (
                  <StarFilled
                    size={20}
                    className="cursor-pointer text-[#3c91ff] transition-colors"
                    onClick={toggleFavorite}
                    tabIndex={0}
                    role="button"
                    aria-pressed={isFavorite}
                    aria-label={t('remove-from-favorites')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleFavorite();
                      }
                    }}
                  />
                ) : (
                  <Star
                    size={20}
                    className="cursor-pointer text-[#4a4a56] transition-colors hover:text-[#0550dc]"
                    onClick={toggleFavorite}
                    tabIndex={0}
                    role="button"
                    aria-pressed={isFavorite}
                    aria-label={t('add-to-favorites')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleFavorite();
                      }
                    }}
                  />
                )}
                <span
                  data-testid="loop-card-title"
                  id={`loop-title-${id}`}
                  className="text-sm font-semibold text-[#4a4a56] dark:text-[#c8c8dd]"
                >
                  {name}
                </span>
              </div>
            )}
            {isDisabled && (
              <span
                data-testid="loop-card-title-disabled"
                id={`loop-title-${id}`}
                className="text-sm font-semibold text-[#4a4a56] dark:text-[#c8c8dd]"
              >
                {name}
              </span>
            )}
          </div>
          <div
            data-testid="loop-card-actions"
            id={`loop-actions-${id}`}
            className="flex items-center gap-4 text-sm"
          >
            {localUpgradable && (
              <button
                type="button"
                className="btn-alert px-3 py-1 text-xs"
                onClick={handleUpgradeClick}
                disabled={!isAdmin || upgradeInProgress}
              >
                {t('upgrade-loop')}
              </button>
            )}
            <button
              id={`view-dashboard-${id}`}
              onClick={viewDashboard}
              className="cursor-pointer border-none bg-transparent p-0 text-sm text-[#525252] hover:underline dark:text-[#c8c8dd]"
              title={t('View Dashboard')}
              type="button"
              aria-label={t('view-dashboard')}
            >
              {t('view-dashboard')}
            </button>
            <span
              id="id-loop-card-actions-separator-first"
              data-testid="loop-card-actions-separator-first"
              className="text-sm text-[#525252] dark:text-[#6f6f6f]"
            >
              |
            </span>
            <button
              data-testid={`id-view-loop-details-button`}
              id={`loop-details-${id}`}
              onClick={() => {
                if (process.env.NODE_ENV !== 'production') {
                  console.warn('[CreatedLoopCards] CLICK Loop Details:', {
                    id,
                    name,
                  });
                }
                setSelectedLoop({ id, name });
                setLoopContext(id, name, teamspaceId);
                navigate('/loop-details', {
                  state: { associatedUserIds: associatedUsers },
                });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedLoop({ id, name });
                  setLoopContext(id, name, teamspaceId);
                  navigate('/loop-details', {
                    state: { associatedUserIds: associatedUsers },
                  });
                }
              }}
              className="cursor-pointer border-none bg-transparent p-0 text-sm text-[#525252] hover:underline dark:text-[#c8c8dd]"
              title={t('loop-details')}
              type="button"
              aria-label={t('view-loop-details')}
            >
              {t('loop-details')}
            </button>
            <span
              id="id-loop-card-actions-separator-second"
              data-testid="loop-card-actions-separator-second"
              className="text-[#525252] dark:text-[#6f6f6f]"
            >
              |
            </span>
            {showAI && !isAnyAIModuleVisible && (
              <button
                type="button"
                className="inline-flex cursor-pointer border-none bg-transparent p-0 transition-opacity hover:opacity-80"
                onClick={handleAIClick}
                aria-label={t('open-ai-chat')}
                title={t('open-ai-chat')}
              >
                <img src={AI} alt="AI" className="size-6" />
              </button>
            )}
          </div>
        </div>
        <div
          data-testid="loop-card-iframe-container"
          id={`loop-iframe-container-${id}`}
          className="relative mt-6"
        >
          <iframe
            id={`loop-iframe-${id}`}
            ref={iframeRef}
            src={sanitizeIframeSrc(overviewDashboard)}
            title="opensearch"
            className="w-full border-none"
            style={{
              height: `${height}px`,
              pointerEvents: 'auto',
              // Hide the OpenSearch loading screen until the dashboard is ready,
              // so only the custom loading indicator is visible while loading.
              opacity: dashboardLoaded ? 1 : 0,
              transition: 'opacity 0.2s ease-out',
            }}
            sandbox="allow-scripts allow-same-origin"
          />
          {(!dashboardLoaded || loadingDisableIds?.includes(id)) && (
            <div
              id="id-skeleton-overlay"
              data-testid="skeleton-overlay"
              className="absolute inset-0 z-10 flex flex-col gap-4 bg-gray-50 p-4 dark:bg-carbon-darkPanel"
            >
              {/* Title skeleton */}
              <Skeleton className="h-5 w-1/3" />
              {/* Chart area skeleton */}
              <Skeleton className="h-48 w-full" />
              {/* Bottom row skeletons */}
              <div
                id="id-loop-card-skeleton-row-first"
                data-testid="loop-card-skeleton-row-first"
                className="flex gap-4"
              >
                <Skeleton className="h-20 w-1/3" />
                <Skeleton className="h-20 w-1/3" />
                <Skeleton className="h-20 w-1/3" />
              </div>
              <div
                id="id-loop-card-skeleton-row-second"
                data-testid="loop-card-skeleton-row-second"
                className="flex gap-4"
              >
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/4" />
              </div>
            </div>
          )}

          <button
            type="button"
            aria-label="Resize"
            className="absolute bottom-0 h-2.5 w-full cursor-ns-resize border-none bg-transparent p-0"
            onMouseDown={handleMouseDown}
          />
        </div>
        <div
          data-testid="loop-card-footer"
          id={`loop-footer-${id}`}
          className="mt-6 flex items-center justify-between overflow-visible"
        >
          <div
            id="id-loop-card-footer-left"
            data-testid="loop-card-footer-left"
            className="flex items-center gap-2 overflow-visible"
          >
            <label
              id={`go-to-label-${id}`}
              className="text-sm text-[#525252] dark:text-[#c8c8dd]"
            >
              {t('go-to')}:
            </label>
            <div
              data-testid="loop-card-goto-dropdown"
              id={`go-to-dropdown-${id}`}
              ref={appDropdownRef}
              className="relative overflow-visible"
            >
              <button
                id={`go-to-button-${id}`}
                className="flex cursor-pointer items-center gap-2 rounded border border-[#e4e4f0] bg-white px-3 py-1 text-sm text-[#525252] transition-colors hover:bg-gray-50 dark:border-[#3f3f4a] dark:bg-[#393939] dark:text-[#c8c8dd] dark:hover:bg-[#4a4a56]"
                onClick={handleAppDropdownToggle}
                title={t('go-to-application')}
                aria-haspopup="listbox"
                aria-expanded={appDropdownOpen}
                aria-label={t('go-to-application')}
              >
                <span
                  id="id-loop-card-goto-selected-label"
                  data-testid="loop-card-goto-selected-label"
                >
                  {selectedAppLabel ?? t('select')}
                </span>
                {appDropdownOpen ? (
                  <ChevronUp size={16} className="text-[#3C91FF]" />
                ) : (
                  <ChevronDown size={16} className="text-[#3C91FF]" />
                )}
              </button>
              {appDropdownOpen && (
                <div
                  id="id-loop-card-goto-dropdown-menu"
                  data-testid="loop-card-goto-dropdown-menu"
                  className="absolute bottom-full left-0 z-50 mb-2 w-56 rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-gray-700 dark:bg-[#393939]"
                  role="listbox"
                  tabIndex={-1}
                >
                  {[
                    {
                      key: 'plan',
                      icon: Plan,
                      label: t('Plan'),
                      license: 'plan',
                      title: t('go-to-plan'),
                      clickLabel: t('plan'),
                    },
                    {
                      key: 'control',
                      icon: SC,
                      label: t('Control'),
                      license: 'control',
                      title: t('go-to-control'),
                      clickLabel: t('control'),
                    },
                    {
                      key: 'deploy',
                      icon: Deploy,
                      label: t('Deploy'),
                      license: 'deploy',
                      title: t('go-to-deploy'),
                      clickLabel: t('deploy'),
                    },
                    {
                      key: 'test',
                      icon: Test,
                      label: t('test'),
                      license: 'test',
                      title: t('go-to-test'),
                      clickLabel: t('test'),
                    },
                    {
                      key: 'release',
                      icon: VS,
                      label: t('Release'),
                      license: 'release',
                      title: t('go-to-release'),
                      clickLabel: t('Release'),
                    },
                    {
                      key: 'measure',
                      icon: VS,
                      label: t('valueStream'),
                      license: 'measure',
                      title: t('go-to-value-stream'),
                      clickLabel: t('valueStream'),
                    },
                    {
                      key: 'build',
                      icon: Build,
                      label: t('build'),
                      license: 'build',
                      title: t('go-to-build'),
                      clickLabel: t('build'),
                    },
                  ].map(({ key, icon, label, license, title, clickLabel }) => {
                    if (!licenses?.includes(license)) return null;
                    return (
                      <button
                        key={key}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        onClick={() => handleAppOptionClick(clickLabel)}
                        type="button"
                        title={title}
                        tabIndex={0}
                        role="option"
                        aria-selected={false}
                        aria-label={title}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleAppOptionClick(clickLabel);
                          }
                        }}
                      >
                        <IconButton
                          icon={icon}
                          label={label}
                          showSeparator={false}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {effectiveIsTeamspaceAdmin && (
            <div
              data-testid="loop-card-settings-dropdown"
              id={`settings-dropdown-${id}`}
              className="relative flex items-center gap-2"
              ref={dropdownRef}
            >
              <button
                id={`settings-icon-${id}`}
                onClick={toggleDropdown}
                className="cursor-pointer border-0 bg-transparent p-0 text-[#525252] transition-colors hover:text-[#3c91ff] dark:text-[#c8c8dd]"
                aria-label={t('Settings')}
                title={t('Settings')}
              >
                <Settings size={20} />
              </button>
              {dropdownVisible && (
                <div
                  data-testid="loop-card-settings-menu"
                  id={`settings-menu-${id}`}
                  className="absolute bottom-full right-0 z-50 mb-1 w-56 rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-gray-700 dark:bg-[#393939]"
                >
                  {!isDisabled && (
                    <button
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
                      onClick={handleEditClick}
                      disabled={!isAdmin}
                    >
                      {t('edit-members')}
                    </button>
                  )}
                  <div
                    id="id-loop-card-settings-menu-divider"
                    data-testid="loop-card-settings-menu-divider"
                    className="border-t border-gray-200 dark:border-gray-700"
                  />
                  {showDisable && (
                    <button
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
                      onClick={() => {
                        if (isFavorite) {
                          toggleFavorite();
                        }
                        handleDisableLoop();
                      }}
                      disabled={!isAdmin}
                    >
                      {isDisabled ? t('Enable') : t('archive-loop')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <EditModal
          isVisible={isEditModalVisible}
          onClose={handleCloseModal}
          loopName={name}
          loopId={id}
          associatedUsers={associatedUsers.map((username) => ({
            username,
            fullname: username,
          }))}
          refreshLoops={refreshLoops}
          showSnackbar={showSnackbarForEditModal}
        />
        <AIModule
          isVisible={isAIModuleVisible}
          onClose={handleCloseAIModule}
          loopId={id}
          availableLoops={[{ id, name }]}
          initialChatHistories={sharedChatHistories}
          setChatHistories={setSharedChatHistories}
        />
        <UpgradeLoopModal
          isOpen={isUpgradeModalOpen}
          onClose={() => {
            setUpgradeModalOpen(false);
            setUpgradeTasks([]);
            setUpgradePreviewTasks([]);
            setUpgradePreviewError('');
            setUpgradePreviewLoading(false);
            // Refresh loops after modal closes to update the loop list
            // This ensures the upgrade button disappears and data is fresh
            refreshLoops();
          }}
          loopId={id}
          loopName={name}
          onUpgrade={handleUpgradeFromModal}
          onAnimationComplete={handleUpgradeAnimationComplete}
          upgradeTasks={upgradeTasks}
          previewTasks={upgradePreviewTasks}
          isPreviewLoading={upgradePreviewLoading}
          previewError={upgradePreviewError}
        />
        <CustomSnackbar
          open={snackbarState.open}
          message={snackbarState.message}
          severity={snackbarState.severity}
          onClose={closeSnackbar}
        />
      </div>
    );
  }
};

// Memoize Card to prevent iframe reloads when parent re-renders without relevant prop changes.
// Custom comparison ignores function identity changes and only checks primitive props that affect rendering.
const shallowEqualArray = (a?: string[], b?: string[]) => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export default memo(Card, (prev, next) => {
  return (
    prev.id === next.id &&
    prev.name === next.name &&
    prev.description === next.description &&
    prev.isAdmin === next.isAdmin &&
    prev.isFavorite === next.isFavorite &&
    prev.isDisabled === next.isDisabled &&
    prev.upgradable === next.upgradable &&
    prev.incompelete === next.incompelete &&
    prev.failed === next.failed &&
    prev.isTeamspaceAdmin === next.isTeamspaceAdmin &&
    prev.isAnyAIModuleVisible === next.isAnyAIModuleVisible &&
    prev.sharedChatHistories === next.sharedChatHistories &&
    shallowEqualArray(prev.associatedUsers, next.associatedUsers) &&
    shallowEqualArray(prev.loadingDisableIds, next.loadingDisableIds)
  );
});
